import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2, Zap } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  AuthLayout,
  PasswordInput,
  GoogleOAuthButton,
  AuthDivider,
} from "@/components/auth";
import { loginSchema, LoginInput } from "@/lib/validations/auth";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { preloadCriticalRoutes } from "@/lib/preload";

export function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, loginWithGoogle } = useAuth();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);

  const from = location.state?.from?.pathname || "/app";

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginInput>({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = async (data: LoginInput) => {
    setIsLoading(true);
    try {
      await login(data.email, data.password);
      // Preload critical routes while showing success toast
      preloadCriticalRoutes();
      toast({
        title: "Welcome back!",
        description: "You have successfully logged in.",
      });
      navigate(from, { replace: true });
    } catch (error: any) {
      const message = error.response?.data?.detail || "Invalid email or password";
      toast({
        title: "Login failed",
        description: typeof message === "string" ? message : "Please check your credentials",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setIsGoogleLoading(true);
    try {
      await loginWithGoogle();
      // Preload critical routes after Google OAuth completes
      preloadCriticalRoutes();
    } catch (error) {
      toast({
        title: "Google login failed",
        description: "Could not connect with Google. Please try again.",
        variant: "destructive",
      });
      setIsGoogleLoading(false);
    }
  };

  return (
    <AuthLayout
      title="Sign in to your account"
      subtitle="Get started in seconds with Google"
    >
      {/* Google OAuth - Primary/Recommended Option */}
      <div className="space-y-3">
        <div className="flex items-center justify-center gap-2">
          <Badge variant="secondary" className="bg-primary/10 text-primary border-primary/20 gap-1">
            <Zap className="h-3 w-3" />
            Recommended
          </Badge>
          <span className="text-xs text-muted-foreground">Instant access</span>
        </div>

        <div className="relative">
          <div className="absolute -inset-1 bg-gradient-to-r from-primary/20 via-accent/20 to-primary/20 rounded-xl blur-sm opacity-75" />
          <div className="relative">
            <GoogleOAuthButton
              onClick={handleGoogleLogin}
              isLoading={isGoogleLoading}
              text="Continue with Google"
            />
          </div>
        </div>

        <p className="text-xs text-center text-muted-foreground">
          No verification email required
        </p>
      </div>

      <AuthDivider text="or sign in with email" />

      {/* Email/Password - Secondary Option */}
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="email">Email <span className="text-destructive">*</span></Label>
          <Input
            id="email"
            type="email"
            placeholder="you@example.com"
            autoComplete="email"
            aria-invalid={!!errors.email}
            aria-describedby={errors.email ? "email-error" : undefined}
            {...register("email")}
            className={errors.email ? "border-red-500" : ""}
          />
          {errors.email && (
            <p id="email-error" role="alert" className="text-sm text-red-500 animate-shake">{errors.email.message}</p>
          )}
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label htmlFor="password">Password <span className="text-destructive">*</span></Label>
            <Link
              to="/forgot-password"
              className="text-sm text-accent hover:underline"
            >
              Forgot password?
            </Link>
          </div>
          <PasswordInput
            id="password"
            placeholder="Enter your password"
            autoComplete="current-password"
            aria-invalid={!!errors.password}
            aria-describedby={errors.password ? "password-error" : undefined}
            {...register("password")}
            error={!!errors.password}
          />
          {errors.password && (
            <p id="password-error" role="alert" className="text-sm text-red-500 animate-shake">{errors.password.message}</p>
          )}
        </div>

        <Button type="submit" variant="outline" className="w-full" disabled={isLoading}>
          {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Sign in with email
        </Button>
      </form>

      <p className="mt-6 text-center text-sm text-muted-foreground">
        Don't have an account?{" "}
        <Link to="/signup" className="text-accent hover:underline font-medium">
          Sign up
        </Link>
      </p>
    </AuthLayout>
  );
}

export default Login;
